'use client';

import { useEffect, useState } from 'react';

const SECOND = 1000;
const MINUTE = 60 * SECOND;
const HOUR = 60 * MINUTE;
const DAY = 24 * HOUR;

export type CountdownFormat = 'dDay' | 'hms';

export interface CountdownState {
  /** 종료 시각까지 남은 ms (음수면 0 으로 clamp). */
  remainingMs: number;
  /** "D-3" / "00:01:23" 형태. */
  text: string;
  /** 종료 시각 도달 여부. */
  isExpired: boolean;
  /** 자동 결정된 포맷 ('dDay' = 24h 이상, 'hms' = 24h 미만). */
  format: CountdownFormat;
}

interface UseCountdownOptions {
  /** 종료 시각 (ms epoch). null/undefined 면 hook 비활성. */
  targetAt: number | null | undefined;
  /** 종료 시각 도달 시 1회 호출. */
  onExpire?: () => void;
  /** 24h 이상이어도 강제로 hms 포맷 사용. 기본 false. */
  forceFormat?: CountdownFormat;
}

const pad2 = (n: number) => String(n).padStart(2, '0');

/** ms epoch 을 그 날 자정(local) 으로 내린다. D-day 캘린더 일수 차 계산용. */
const startOfDay = (ms: number): number => {
  const d = new Date(ms);
  d.setHours(0, 0, 0, 0);
  return d.getTime();
};

/**
 * D-N = 마감일과 오늘의 캘린더 날짜 차 (자정 기준).
 *
 * NOTE(SID-4162): 잔여 ms 의 `Math.ceil` 은 마감 시각이 당일 23:59:59 일 때
 * partial-day 만큼 +1 overshoot 했다 (오늘 6/10 · 마감 6/17 → D-8). 자정 기준
 * 날짜 차로 계산하면 정확한 D-7 이 나오고, 마감 당일은 D-0 이 된다.
 */
const formatDDay = (targetAt: number, now: number): string => {
  const days = Math.round((startOfDay(targetAt) - startOfDay(now)) / DAY);
  return `D-${Math.max(days, 0)}`;
};

const formatHms = (remainingMs: number): string => {
  const totalSeconds = Math.max(0, Math.floor(remainingMs / SECOND));
  const hours = Math.floor(totalSeconds / 3600);
  const minutes = Math.floor((totalSeconds % 3600) / 60);
  const seconds = totalSeconds % 60;
  return `${pad2(hours)}:${pad2(minutes)}:${pad2(seconds)}`;
};

const computeState = (targetAt: number, forceFormat?: CountdownFormat): CountdownState => {
  const now = Date.now();
  const remainingMs = Math.max(0, targetAt - now);
  const isExpired = remainingMs <= 0;
  const format: CountdownFormat = forceFormat ?? (remainingMs >= DAY ? 'dDay' : 'hms');
  const text = format === 'dDay' ? formatDDay(targetAt, now) : formatHms(remainingMs);
  return { remainingMs, text, isExpired, format };
};

/**
 * SSR/CSR 시점 차로 인한 hydration mismatch 회피용 placeholder.
 * 서버/클라이언트 첫 렌더 모두 동일한 "00:00:00" 으로 페인트한 뒤,
 * mount 직후 useEffect 의 첫 tick 에서 실제 remaining 값으로 교체.
 */
const PLACEHOLDER_STATE: CountdownState = {
  remainingMs: 0,
  text: '00:00:00',
  isExpired: false,
  format: 'hms',
};

/**
 * targetAt(ms epoch) 까지 남은 시간을 1초 단위로 갱신.
 * 24h 이상이면 D-day, 미만이면 hh:mm:ss 형식으로 자동 분기.
 */
export const useCountdown = ({
  targetAt,
  onExpire,
  forceFormat,
}: UseCountdownOptions): CountdownState | null => {
  const [state, setState] = useState<CountdownState | null>(() => {
    if (targetAt == null) return null;
    return PLACEHOLDER_STATE;
  });

  useEffect(() => {
    if (targetAt == null) {
      setState(null);
      return;
    }

    let cancelled = false;
    let expiredFired = false;

    const tick = () => {
      if (cancelled) return;
      const next = computeState(targetAt, forceFormat);
      setState(next);
      if (next.isExpired && !expiredFired) {
        expiredFired = true;
        onExpire?.();
      }
    };

    tick();
    const id = window.setInterval(tick, SECOND);
    return () => {
      cancelled = true;
      window.clearInterval(id);
    };
  }, [targetAt, onExpire, forceFormat]);

  return state;
};
